import React, { useContext, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import OfficeForOfficeManagerListCard from '../cards/office-office-manager-card.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import { Center } from '../../../core/components/center/center.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment } from '../../../core/components/flex/flex.component';
import notify from '../../../core/components/notify/notify.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import type Office from '../../offices/models/office';
import Input from '../../../core/components/input/input.component';
import Grid from '../../../core/components/grid/grid.component';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly handleCreate: (email: string, officesIds: string[]) => void;
  readonly offices: Office[] | undefined;
}

const CreateOfficeManagerModal = ({ showModal, handleClose, handleCreate, offices }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>('');
  const [officesIdsSelected, setOfficesIdsSelected] = useState<string[]>([]);

  const handleCloseModal = (): void => {
    setEmail('');
    setOfficesIdsSelected([]);
    handleClose();
  };

  const officeManagerIsValid = (emailToRegister: string, officesIds: string[]): boolean => {
    if (stringIsNullOrWhitespace(emailToRegister)) {
      notify(t('email-empty-message'), false);
      return false;
    }

    if (officesIds.length === 0) {
      notify(t('no-office-selected-message'), false);
      return false;
    }

    return true;
  };

  const handleSave = (): void => {
    if (!officeManagerIsValid(email, officesIdsSelected)) {
      return;
    }
    handleCreate(email, officesIdsSelected);
    handleCloseModal();
  };

  const removeOfficeIdFromOfficesIdsSelected = (selectedOfficeId: string): void => {
    const remainingOffices = officesIdsSelected.filter((officeId) => officeId !== selectedOfficeId);
    setOfficesIdsSelected(remainingOffices);
  };

  const handleSelectedOfficeChange = (selectedOffice: Office): void => {
    if (!selectedOffice.id) {
      return;
    }

    if (officesIdsSelected.some((officeId) => officeId === selectedOffice.id)) {
      removeOfficeIdFromOfficesIdsSelected(selectedOffice.id);
      return;
    }

    const officesSelectedCopy: string[] = Object.assign([], officesIdsSelected);
    officesSelectedCopy.push(selectedOffice.id);
    setOfficesIdsSelected(officesSelectedCopy);
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCloseModal} style={styles.modalStyle(isMobile)}>
      <Column crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <span className={style(styles.columnHeader)}>{t('register-office-manager').toUpperCase()}</span>
        <span className={style(styles.modalTitle)}>{t('email-address')}</span>
        <Input placeholder={t('email').toLowerCase()} value={email} onChange={setEmail} onEnter={handleSave} />
        {((): React.JSX.Element => {
          if (offices == null) {
            return (
              <Center>
                <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
              </Center>
            );
          }

          if (offices?.length === 0) {
            return <span className={style(styles.emptyListMessage)}>{t('no-offices-available')}</span>;
          }

          return (
            <>
              <span className={style(styles.modalTitle)}>{t('offices')}</span>
              <Grid
                rowClass={style(styles.gridRow)}
                numberOfColumns={1}
                itemSpacing={12}
                numberOfItems={offices.length}
                renderItem={(index): React.JSX.Element => {
                  const office = offices[index];
                  return (
                    office && (
                      <div className={isMobile ? style(styles.rowItem(true)) : undefined} key={office.id}>
                        <OfficeForOfficeManagerListCard
                          office={office}
                          selectedOfficeChange={handleSelectedOfficeChange}
                          officesIdsSelected={officesIdsSelected}
                        />
                      </div>
                    )
                  );
                }}
              />
            </>
          );
        })()}
        <div className={style(styles.button)}>
          <PrimaryButton fullWidth onClick={handleSave}>
            {t('register')}
          </PrimaryButton>
        </div>
      </Column>
    </Modal>
  );
};

export default CreateOfficeManagerModal;
